/**
 * Responsive utility functions and breakpoint helpers
 */

// Common responsive spacing values
export const responsiveSpacing = {
  xs: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 5,
};

// Responsive typography scales
export const responsiveTypography = {
  h1: {
    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
    lineHeight: { xs: 1.2, sm: 1.3 },
  },
  h2: {
    fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
    lineHeight: { xs: 1.2, sm: 1.3 },
  },
  h3: {
    fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
    lineHeight: { xs: 1.2, sm: 1.3 },
  },
  h4: {
    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
    lineHeight: { xs: 1.2, sm: 1.3 },
  },
  h5: {
    fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" },
    lineHeight: { xs: 1.2, sm: 1.3 },
  },
  h6: {
    fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
    lineHeight: { xs: 1.2, sm: 1.3 },
  },
  body1: {
    fontSize: { xs: "0.875rem", sm: "1rem" },
    lineHeight: { xs: 1.5, sm: 1.6 },
  },
  body2: {
    fontSize: { xs: "0.75rem", sm: "0.875rem" },
    lineHeight: { xs: 1.4, sm: 1.5 },
  },
};

// Container max widths for different page types
export const containerSizes = {
  form: "sm", // 600px - for login, register forms
  content: "lg", // 1200px - for general content pages
  dashboard: "xl", // 1536px - for dashboards with lots of content
  admin: "xl", // 1536px - for admin pages
};

// Responsive padding/margin helpers
export const responsivePadding = {
  page: { xs: 2, sm: 3, md: 4 }, // Main page padding
  container: { xs: 1, sm: 2, md: 3 }, // Container internal padding
  card: { xs: 2, sm: 2.5, md: 3 }, // Card padding
  cardContent: { xs: 1.5, sm: 2, md: 2.5 }, // Card content padding
  section: { xs: 2, sm: 3, md: 4 }, // Section spacing
};

export const responsiveMargin = {
  section: { xs: 2, sm: 3, md: 4 }, // Between major sections
  element: { xs: 1.5, sm: 2, md: 2.5 }, // Between elements
  small: { xs: 0.8, sm: 1, md: 1.2 }, // Small gaps
  tiny: { xs: 0.5, sm: 0.7, md: 1 }, // Very small gaps
};

// Grid spacing helpers
export const gridSpacing = {
  tight: { xs: 1, sm: 1.5, md: 2 },
  normal: { xs: 2, sm: 2.5, md: 3 },
  loose: { xs: 3, sm: 3.5, md: 4 },
  cards: { xs: 2, sm: 2.5, md: 3 }, // Specific for product cards
};

// Button size helpers
export const getButtonSize = () => {
  return window.innerWidth < 600 ? "medium" : "large";
};

export const getTextFieldSize = () => {
  return window.innerWidth < 600 ? "small" : "medium";
};

// Responsive card heights
export const cardHeights = {
  small: { xs: 140, sm: 160, md: 180 },
  medium: { xs: 180, sm: 200, md: 220 },
  large: { xs: 220, sm: 240, md: 260 },
  product: { xs: 520, sm: 540, md: 560 }, // Specific for product cards
};

// Common responsive styles for forms
export const formStyles = {
  container: {
    maxWidth: containerSizes.form,
    px: responsivePadding.container,
  },
  paper: {
    mt: { xs: 4, sm: 8 },
    p: responsivePadding.card,
  },
  title: {
    ...responsiveTypography.h4,
    mb: responsiveMargin.element,
  },
  fieldSpacing: {
    mb: responsiveMargin.element,
  },
  button: {
    py: { xs: 1, sm: 1.5 },
    mb: responsiveMargin.small,
  },
};

// Common responsive styles for content pages
export const pageStyles = {
  container: {
    maxWidth: containerSizes.content,
    mt: responsiveMargin.element,
    mb: responsiveMargin.section,
    px: responsivePadding.container,
  },
  title: {
    ...responsiveTypography.h4,
    mb: responsiveMargin.element,
  },
  section: {
    mb: responsiveMargin.section,
  },
};

// Common responsive styles for admin pages
export const adminPageStyles = {
  container: {
    maxWidth: containerSizes.admin,
    mt: responsiveMargin.element,
    mb: responsiveMargin.section,
    px: responsivePadding.container,
  },
  title: {
    ...responsiveTypography.h4,
    mb: responsiveMargin.element,
  },
  grid: {
    spacing: gridSpacing.normal,
  },
  card: {
    p: responsivePadding.card,
    height: cardHeights.medium,
  },
};

// Responsive navigation styles
export const navStyles = {
  toolbar: {
    px: responsivePadding.container,
  },
  title: {
    ...responsiveTypography.h6,
  },
  menu: {
    mt: 1,
    minWidth: { xs: 200, sm: 250 },
  },
};

// Responsive table styles
export const tableStyles = {
  container: {
    overflowX: "auto",
  },
  cell: {
    px: { xs: 1, sm: 2 },
    fontSize: { xs: "0.75rem", sm: "0.875rem" },
  },
  actionCell: {
    minWidth: { xs: 100, sm: 150 },
  },
};

// Responsive dialog/modal styles
export const dialogStyles = {
  paper: {
    m: { xs: 1, sm: 2 },
    maxWidth: { xs: "calc(100% - 16px)", sm: 600 },
  },
  title: {
    ...responsiveTypography.h5,
    pb: 1,
  },
  content: {
    px: { xs: 2, sm: 3 },
    py: 1,
  },
  actions: {
    px: { xs: 2, sm: 3 },
    py: { xs: 1, sm: 2 },
    gap: 1,
  },
};
